import {
  createIamClient,
  DeleteOperation,
  deleteRequestFactory,
  FindOperation,
  getRequestFactory,
  postRequestFactory,
  putRequestFactory,
  SaveOperation,
  SearchOperation,
  searchRequestFactory
} from '@infuse/api/common';
import { User, CreateUser, UpdateUser, CreateMachineUser } from '@infuse/iam/user/type';

const client = createIamClient('/rest/users');

export const userPermissions = {
  SEARCH: 'iam.search-users',
  GET: 'iam.get-user',
  CREATE: 'iam.create-user',
  UPDATE: 'iam.update-user',
  DELETE: 'iam.delete-user',
  ADD_ROLES: 'iam.add-roles-to-user',
  FORCE_CHANGE_PASSWORD: 'iam.force-change-password',
  FORGOT_PASSWORD: 'iam.forgot-password',
  ACTIVATE: 'iam.activate-user',
  DEACTIVATE: 'iam.deactivate-user'
};

// no pagination or filtering available for this api, so we opt for a simple get all request
export const searchUsers: SearchOperation<User> = searchRequestFactory<User>(client, {
  permissionId: userPermissions.SEARCH
});
export const getUser: FindOperation<User, string> = getRequestFactory<User, string>(client, {
  permissionId: userPermissions.GET
});

export const createUser: SaveOperation<Omit<CreateUser, 'roles'>, User> = postRequestFactory<
  Omit<CreateUser, 'roles'>,
  never
>(client, { pattern: '', permissionId: userPermissions.CREATE });
export const updateUser: SaveOperation<Omit<UpdateUser, 'roles' | 'email'>, User> = putRequestFactory<
  Omit<UpdateUser, 'roles' | 'email'>,
  never
>(client, { pattern: '/{userId}', permissionId: userPermissions.UPDATE });
export const deleteUser: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{userId}',
  permissionId: userPermissions.DELETE
});

// Roles
export const addRolesToUser: SaveOperation<{ roleIds: string[]; replace: boolean }, never> = postRequestFactory<
  {
    roleIds: string[];
    replace: boolean;
  },
  never
>(client, { pattern: '/{userId}/add-roles', permissionId: userPermissions.ADD_ROLES });

export const forceChangePassword: SaveOperation<{ tempPassword: string }, never> = postRequestFactory<
  { tempPassword: string },
  never
>(client, { pattern: '/{userId}/force-change-password', permissionId: userPermissions.FORCE_CHANGE_PASSWORD });

export const forgotPassword: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{userId}/forgot-password',
  permissionId: userPermissions.FORGOT_PASSWORD
});

export const activateUser: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{userId}/activate',
  permissionId: userPermissions.ACTIVATE
});

export const deactivateUser: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{userId}/deactivate',
  permissionId: userPermissions.DEACTIVATE
});

const machineClientBase = createIamClient('/rest/machine-clients');

export const machineClientPermissions = {
  CREATE: 'iam.create-machine-client'
};

export const createMachineClient: SaveOperation<Omit<CreateMachineUser, 'roles'>, User> = postRequestFactory<
  Omit<CreateMachineUser, 'roles'>,
  User
>(machineClientBase, { pattern: '', permissionId: machineClientPermissions.CREATE });
